// core/services/firebase/notificationService.ts
import { 
  collection, 
  doc,
  addDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from './config';

export interface Notification {
  id?: string;
  type: 'like' | 'comment' | 'follow';
  fromUserId: string;
  fromUserHandle: string;
  fromUserName: string;
  fromUserAvatar?: string;
  targetId: string;
  targetType: 'post' | 'profile';
  message: string;
  read: boolean;
  createdAt: Date;
}

export const notificationService = {
  /**
   * Create a new notification
   */
  async createNotification(userId: string, notification: Omit<Notification, 'id' | 'createdAt' | 'read'>): Promise<void> {
    try {
      await addDoc(collection(db, `notifications/${userId}/items`), {
        ...notification,
        read: false,
        createdAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  },

  /**
   * Get user notifications
   */
  async getUserNotifications(userId: string, limit: number = 20): Promise<Notification[]> {
    try {
      const q = query(
        collection(db, `notifications/${userId}/items`),
        orderBy('createdAt', 'desc'),
        firestoreLimit(limit)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      } as Notification));
    } catch (error) {
      console.error('Error getting notifications:', error);
      return [];
    }
  },

  /**
   * Get unread count
   */
  async getUnreadCount(userId: string): Promise<number> {
    try {
      const q = query(
        collection(db, `notifications/${userId}/items`),
        where('read', '==', false)
      );

      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  },

  /**
   * Mark notification as read
   */
  async markAsRead(userId: string, notificationId: string): Promise<void> {
    try {
      const notifRef = doc(db, `notifications/${userId}/items/${notificationId}`);
      await updateDoc(notifRef, {
        read: true
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(userId: string): Promise<void> {
    try {
      const q = query(
        collection(db, `notifications/${userId}/items`),
        where('read', '==', false)
      );

      const snapshot = await getDocs(q);
      const batch = writeBatch(db);

      snapshot.docs.forEach(document => {
        batch.update(document.ref, { read: true });
      });

      await batch.commit();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }
};
